import { useState, createContext } from 'react';
import WithErrorHandling, { RequestError } from 'src/components/hoc/withErrorHandling';
import EmailNotificationsPage from 'src/modules/unsubscribe/emailNotificationsPage';
import { fetchEmailPreferences } from 'src/api/emailNotifications';
import { NotificationPreference, PageContext } from 'src/constants/types';
import { createAuthenticatedApiInstance } from 'src/utils/api';

interface NotificationsContextData {
  notifications: NotificationPreference[],
  token?: string,
  refreshData: (updates: NotificationPreference[]) => void,
}

export const NotificationsContext = createContext<NotificationsContextData>({
  notifications: [],
  token: undefined,
  refreshData: () => {},
});

type Props = {
  token?: string,
  emailNotifications: NotificationPreference[],
};

const Page = ({ token, emailNotifications }: Props) => {
  const [notifications, setNotifications] = useState(emailNotifications);

  const refreshData = (updates: NotificationPreference[]) => {
    setNotifications(notifications.map((option) => updates.find(({ notificationId }) => notificationId === option.notificationId) || option));
  };

  return (
    <NotificationsContext.Provider value={{ notifications, token, refreshData }}>
      <EmailNotificationsPage />
    </NotificationsContext.Provider>
  );
};

Page.getInitialProps = async ({ query: { token }, store: { getState } }: PageContext) => {
  const { currentUser } = getState().context;

  if (!token && !currentUser) {
    throw new RequestError('This page is not available to visitors', 401);
  }

  const api = createAuthenticatedApiInstance(getState);

  const emailNotifications = await fetchEmailPreferences(api, token, currentUser?.userId);

  if (!emailNotifications) {
    throw new RequestError('Invalid page parameters', 404);
  }

  return {
    token,
    emailNotifications,
  };
};

export default WithErrorHandling(Page);
